import { tailwindColors } from '@repo/tailwind-config/tailwind.vars'
import { colorsMapping } from '@repo/toolkit/colors'
import { useNavigator } from '@repo/toolkit/use-navigator'
import Badge from '@repo/ui/badge'
import Button from '@repo/ui/button'
import { PharmacyIcon, StarFilledIcon, UserFiveIcon } from '@repo/ui/icons'
import TextLink from '@repo/ui/text-link'
import Typography from '@repo/ui/typography'
import { useMutation } from '@tanstack/react-query'
import { toast } from 'sonner'

import { constructClientPath } from '../../../../../../packages/toolkit/src/utils/functions/construct-path'
import { connectAsClient } from '../../../api/enrollments/connect-as-client'
import { updateEnrollment } from '../../../api/enrollments/update-enrollment'
import { Paths } from '../../../lib/constants/path'
import { applicationStatusEnumMapping } from '../../../lib/types/enums/enrollment/application-status.enum'
import {
  MedicalStatusEnum,
  medicalStatusEnumMapping
} from '../../../lib/types/enums/enrollment/medical-status.enum'
import { EnrollmentProfileContextType } from '../../../lib/types/fields/enrollments/enrollment'
import { EnrollmentProperties } from '../../../lib/types/models/enrollment/enrollment.properties'
import { EnrollmentInput } from '../../../lib/types/models/enrollment/enrollment-input'
import { JourneyProperties } from '../../../lib/types/models/product/journey.properties'
import { UpdateInput } from '../../../lib/types/utils'
import { showAxiosError } from '../../../lib/utils/functions/misc.functions'
import { constructCenterPath } from '../../../views/centers/utils'
import CountryFlag from '../../elements/country-flag'
import InformationToolTip from '../../elements/information-tooltip'
import { SecureProfileImageInput } from '../../elements/inputs/secure-profile-image-input'
import CancelEnrollment from '../enrollments/cancel-enrollment'
import DeaffectEnrollmentCenter from '../enrollments/deaffect-enrollment-center'

type Props = {
  enrollment: EnrollmentProfileContextType
  onEnrollmentUpdate?: () => void
}

const EnrollmentProfileHeader: React.FC<Props> = ({ enrollment, onEnrollmentUpdate }) => {
  const { navigate } = useNavigator()

  const { mutateAsync, isPending } = useMutation({
    mutationFn: (input: UpdateInput<EnrollmentInput>) => updateEnrollment(input, ['id']),
    onError: showAxiosError,
    onSuccess: () => {
      onEnrollmentUpdate?.()
      toast.success('Profil mis à jour avec succès')
    }
  })

  const connectAsClientMutation = useMutation({
    mutationFn: connectAsClient,
    onSuccess: () => {
      // A full page load: nothing of the previous session keeps running
      window.location.assign(constructClientPath(Paths.CLIENT_PERSONAL_SPACE_HOME, enrollment.id))
    },
    onError: showAxiosError
  })

  // a One to One Individuel enrollment has no journey: its stay dates live on its own proposition
  const stayDates = EnrollmentProperties.getEnrollmentDepartureAndArrivalDates(enrollment).value
  const isCurrentlyInStay =
    !!stayDates &&
    JourneyProperties.isCurrentlyInStay({
      date: { departureDate: stayDates.startDate, returnDate: stayDates.endDate }
    })
  const center = enrollment.enrollmentCenter?.center

  return (
    <div className="p-4 flex justify-between border border-grey-light rounded-lg">
      <div className="flex items-center gap-4">
        <SecureProfileImageInput
          title="Photo du jeune"
          file={enrollment.secureIdentityPicture}
          isLoading={isPending}
          onFileChange={async (file) => {
            await mutateAsync({ id: enrollment.id, secureIdentityPicture: { file } })
          }}
        />
        <div>
          <div className="flex items-center gap-3">
            <Typography variant="text">
              {EnrollmentProperties.getEnrollmentName(enrollment)}
            </Typography>

            {center &&
              (isCurrentlyInStay ? (
                <Badge
                  text={
                    <div className="flex gap-1 items-center">
                      <Typography variant="label-semibold" color={tailwindColors.fushia.DEFAULT}>
                        En séjour actuellement -
                      </Typography>
                      <TextLink
                        name={center.name}
                        href={constructCenterPath(Paths.CENTER_GLOBAL_VIEW_PAGE, center.id)}
                      />
                    </div>
                  }
                />
              ) : (
                <Badge
                  variant="blue"
                  size="medium"
                  color="transparent"
                  borderColor={tailwindColors.blue.light.DEFAULT}
                  text={enrollment.enrollmentCenter?.center.name}
                />
              ))}
          </div>
          <div className="h-3" />
          <div className="flex items-center gap-1">
            <Badge
              text={applicationStatusEnumMapping[enrollment.status].label}
              variant="success"
              iconPosition="start"
              icon={
                <UserFiveIcon
                  className="w-5 h-5"
                  filled={false}
                  color={tailwindColors.green.DEFAULT}
                />
              }
            />
            {enrollment.isAA && (
              <Badge variant="yellow" icon={<StarFilledIcon className="w-5 h-5" />} />
            )}

            {enrollment.medicalStatus !== MedicalStatusEnum.RAS && (
              <Badge
                variant={medicalStatusEnumMapping[enrollment.medicalStatus].variant}
                icon={
                  <PharmacyIcon
                    color={
                      colorsMapping[
                        medicalStatusEnumMapping[enrollment.medicalStatus].variant ?? 'grey'
                      ].text
                    }
                    color2={
                      colorsMapping[
                        medicalStatusEnumMapping[enrollment.medicalStatus].variant ?? 'grey'
                      ].hover
                    }
                  />
                }
              />
            )}
            <div className="p-2 rounded border border-grey-light bg-white-hover flex items-center gap-2">
              <CountryFlag code={EnrollmentProperties.getEnrollmentCountry(enrollment).value} />
              <Typography variant="label-semibold">
                {EnrollmentProperties.getEnrollmentLabel(enrollment)}
              </Typography>
            </div>
          </div>
        </div>
      </div>

      <div className="flex items-start gap-2">
        {!EnrollmentProperties.isCancelled(enrollment) && (
          <CancelEnrollment
            id={enrollment.id}
            paymentStatus={enrollment.paymentStatus}
            isConfirmed={EnrollmentProperties.isConfirmed(enrollment)}
            onSuccess={() => navigate(Paths.ENROLLMENT_VALIDATED_LIST_PAGE)}
          />
        )}
        {enrollment.enrollmentCenter?.center.id && (
          <DeaffectEnrollmentCenter
            id={enrollment.id}
            onSuccess={() => {
              const centerId = enrollment.enrollmentCenter?.center.id
              if (centerId)
                navigate(constructCenterPath(Paths.CENTER_YOUNG_PEOPLE_PAGE, centerId), {
                  replace: true
                })
            }}
            centerId={enrollment.enrollmentCenter.center.id}
          />
        )}
        <div className="flex items-center gap-1">
          <Button
            variant="ghost"
            disabled={connectAsClientMutation.isPending || !enrollment.canConnectAsClient}
            label={`Voir l'espace client`}
            className="text-nowrap"
            loading={connectAsClientMutation.isPending}
            onClick={() => connectAsClientMutation.mutate(enrollment.id)}
          />
          {!enrollment.canConnectAsClient && (
            <InformationToolTip size="small" variant="secondary" align="end">
              <Typography variant="label-medium" color={tailwindColors.white.DEFAULT}>
                Aucun compte client n'est lié à cette inscription : le parent n'a pas encore créé
                son compte, ou l'email du parent appartient à un compte qui n'est pas un compte
                client.
              </Typography>
            </InformationToolTip>
          )}
        </div>
      </div>
    </div>
  )
}

export default EnrollmentProfileHeader
